/**
 * PaperLayoutTab — visual drag-and-drop form designer.
 *
 * Lets the form author pick a paper size, then drag/drop existing fields onto
 * a paper canvas and resize them. Position is persisted on each field as
 * `canvas_position: { x, y, w, h }` (pixels, 96px = 1 inch).
 *
 * Two add modes live in the left palette:
 *   - "Text Label" (info)         — decorative label.
 *   - "Reference Label" (field_reference) — pulls a value from another field
 *     in the same form; the author picks the source field via dropdown.
 *
 * Fillable rendering: when `paper_layout.enabled` is true, the fill page
 * surfaces a "Paper view" toggle that renders inputs at their canvas
 * coordinates (read-only positions, editable values).
 */
import React, { useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Type, Hash, Calendar, Mail, Phone, List as ListIcon, CheckSquare, Circle,
  AlignLeft, PenTool, FileText, Trash2, Move, Tag, AtSign,
} from 'lucide-react';

// Paper sizes in pixels at 96 DPI
const PAPER_SIZES = {
  letter:  { w: 816,  h: 1056, label: 'Letter (8.5 × 11 in)' },
  legal:   { w: 816,  h: 1344, label: 'Legal (8.5 × 14 in)' },
  a4:      { w: 794,  h: 1123, label: 'A4 (210 × 297 mm)' },
  tabloid: { w: 1056, h: 1632, label: 'Tabloid (11 × 17 in)' },
  custom:  { w: 816,  h: 1056, label: 'Custom dimensions' },
};

const ADDABLE_TYPES = [
  { value: 'text',      label: 'Text input',     icon: Type },
  { value: 'number',    label: 'Number',         icon: Hash },
  { value: 'date',      label: 'Date',           icon: Calendar },
  { value: 'email',     label: 'Email',          icon: Mail },
  { value: 'phone',     label: 'Phone',          icon: Phone },
  { value: 'select',    label: 'Dropdown',       icon: ListIcon },
  { value: 'checkbox',  label: 'Checkbox',       icon: CheckSquare },
  { value: 'radio',     label: 'Radio',          icon: Circle },
  { value: 'textarea',  label: 'Text area',      icon: AlignLeft },
  { value: 'signature', label: 'Signature',      icon: PenTool },
  { value: 'info',      label: 'Text label',     icon: Tag },
  { value: 'field_reference', label: 'Reference label', icon: AtSign },
];

const DEFAULT_LAYOUT = {
  enabled: true,
  page_size: 'letter',
  orientation: 'portrait',
  custom_width: 816,
  custom_height: 1056,
  snap: true,
  grid: 8,
};

function paperDims(layout) {
  const base = PAPER_SIZES[layout.page_size] || PAPER_SIZES.letter;
  const w = layout.page_size === 'custom' ? layout.custom_width : base.w;
  const h = layout.page_size === 'custom' ? layout.custom_height : base.h;
  return layout.orientation === 'landscape' ? { w: h, h: w } : { w, h };
}

function snapValue(v, grid, on) {
  if (!on || !grid) return Math.round(v);
  return Math.round(v / grid) * grid;
}

function genId(type) {
  return `${type}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function PaperLayoutTab({ fields, setFields, paperLayout, setPaperLayout }) {
  const layout = paperLayout || DEFAULT_LAYOUT;
  const dims = useMemo(() => paperDims(layout), [layout]);
  const canvasRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(0.85);
  // active drag/resize op
  const dragRef = useRef(null);

  const updateLayout = (patch) => setPaperLayout({ ...layout, ...patch });

  const setFieldPos = (idx, pos) => {
    const next = [...fields];
    next[idx] = { ...next[idx], canvas_position: { ...(next[idx].canvas_position || {}), ...pos } };
    setFields(next);
  };

  const addFieldAtCenter = (type) => {
    const w = type === 'textarea' ? 320 : type === 'signature' ? 280 : type === 'info' ? 240 : 200;
    const h = type === 'textarea' ? 96 : type === 'signature' ? 72 : type === 'info' ? 32 : 40;
    const x = snapValue((dims.w - w) / 2, layout.grid, layout.snap);
    const y = snapValue((dims.h - h) / 2, layout.grid, layout.snap);
    const fid = genId(type);
    const f = {
      field_id: fid,
      label: type === 'info' ? 'Section title' : type === 'field_reference' ? 'Reference label' : 'New field',
      field_type: type,
      required: false,
      order: fields.length,
      width: 'full',
      canvas_position: { x, y, w, h, page: 0 },
    };
    setFields([...fields, f]);
    setSelectedId(fid);
  };

  const removeField = (idx) => {
    setFields(fields.filter((_, i) => i !== idx));
    setSelectedId(null);
  };

  /**
   * Auto-place every field on the canvas using a simple flow layout.
   *
   * Walks the existing field list top-to-bottom and lays each one out
   * respecting `width: full|half|third`. Multi-column / section / table
   * fields each get their own row. Skips fields that already have a
   * canvas_position (so re-running is safe and never trashes prior work).
   */
  const autoLayoutAll = (overwrite = false) => {
    const M = 48; // page margin in px
    const GAP_X = 12;
    const GAP_Y = 12;
    const usableW = dims.w - M * 2;
    const rowHeightFor = (f) => {
      if (f.field_type === 'textarea') return 96;
      if (f.field_type === 'signature') return 80;
      if (f.field_type === 'section') return 36;
      if (f.field_type === 'info') return 36;
      if (f.field_type === 'multi_column') return 120;
      if (f.field_type === 'checkbox_table' || f.field_type === 'calc_table') return 140;
      if (f.field_type === 'fill_blank') return 64;
      return 56;
    };
    const widthFor = (f) => {
      if (['multi_column', 'section', 'checkbox_table', 'calc_table', 'textarea', 'signature', 'fill_blank'].includes(f.field_type)) return usableW;
      if (f.width === 'half') return Math.floor((usableW - GAP_X) / 2);
      if (f.width === 'third') return Math.floor((usableW - 2 * GAP_X) / 3);
      return usableW;
    };
    let cursorY = M;
    let rowX = M;
    let rowRemaining = usableW;
    let rowMaxH = 0;
    const next = fields.map((f) => {
      if (f.canvas_position && !overwrite) return f;
      const w = widthFor(f);
      const h = rowHeightFor(f);
      // Force new row for full-width or when it won't fit
      if (w >= usableW - 1 || w > rowRemaining + 0.5) {
        if (rowX !== M) { cursorY += rowMaxH + GAP_Y; }
        rowX = M;
        rowRemaining = usableW;
        rowMaxH = 0;
      }
      const x = snapValue(rowX, layout.grid, layout.snap);
      const y = snapValue(cursorY, layout.grid, layout.snap);
      const W = snapValue(w, layout.grid, layout.snap);
      const H = snapValue(h, layout.grid, layout.snap);
      const placed = { ...f, canvas_position: { x, y, w: W, h: H, page: 0 } };
      if (W >= usableW - 1) {
        // full-width — close the row
        cursorY += H + GAP_Y;
        rowX = M;
        rowRemaining = usableW;
        rowMaxH = 0;
      } else {
        rowX += W + GAP_X;
        rowRemaining -= W + GAP_X;
        rowMaxH = Math.max(rowMaxH, H);
        if (rowRemaining < 80) {
          cursorY += rowMaxH + GAP_Y;
          rowX = M;
          rowRemaining = usableW;
          rowMaxH = 0;
        }
      }
      return placed;
    });
    setFields(next);
  };

  // unify mouse + touch events
  const pointerXY = (e) => {
    if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const onPointerDown = (e, idx, mode) => {
    e.stopPropagation();
    const f = fields[idx];
    const pos = f.canvas_position || { x: 0, y: 0, w: 200, h: 40 };
    const { x: px, y: py } = pointerXY(e);
    dragRef.current = { idx, mode, startX: px, startY: py, origPos: { ...pos } };
    setSelectedId(f.field_id);
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp);
  };

  const onPointerMove = (e) => {
    const op = dragRef.current;
    if (!op) return;
    if (e.touches) e.preventDefault();
    const { x: px, y: py } = pointerXY(e);
    const dx = (px - op.startX) / zoom;
    const dy = (py - op.startY) / zoom;
    let next = { ...op.origPos };
    if (op.mode === 'move') {
      next.x = Math.max(0, Math.min(dims.w - next.w, op.origPos.x + dx));
      next.y = Math.max(0, Math.min(dims.h - next.h, op.origPos.y + dy));
    } else if (op.mode === 'resize') {
      next.w = Math.max(40, Math.min(dims.w - next.x, op.origPos.w + dx));
      next.h = Math.max(20, Math.min(dims.h - next.y, op.origPos.h + dy));
    }
    next.x = snapValue(next.x, layout.grid, layout.snap);
    next.y = snapValue(next.y, layout.grid, layout.snap);
    next.w = snapValue(next.w, layout.grid, layout.snap);
    next.h = snapValue(next.h, layout.grid, layout.snap);
    setFieldPos(op.idx, next);
  };

  const onPointerUp = () => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onPointerMove);
    window.removeEventListener('mouseup', onPointerUp);
    window.removeEventListener('touchmove', onPointerMove);
    window.removeEventListener('touchend', onPointerUp);
  };

  const selectedIdx = fields.findIndex(f => f.field_id === selectedId);
  const selected = selectedIdx >= 0 ? fields[selectedIdx] : null;
  const referenceCandidates = fields.filter(f => f.field_id !== selectedId && !['section', 'multi_column', 'signature', 'info', 'field_reference', 'file'].includes(f.field_type));

  const gridBg = layout.snap && layout.grid
    ? `linear-gradient(to right, rgba(14,116,144,0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(14,116,144,0.07) 1px, transparent 1px)`
    : 'none';

  return (
    <div className="grid lg:grid-cols-[260px_1fr_300px] gap-4" data-testid="paper-layout-tab">
      {/* Left: paper settings + palette */}
      <div className="space-y-3">
        <div className="careiq-card p-3 rounded-lg border border-[#E2E8F0] bg-white">
          <div className="text-xs font-semibold text-[#0A2342] mb-2">Paper</div>
          <div className="space-y-2">
            <div>
              <Label className="text-[11px] text-[#64748B]">Size</Label>
              <Select value={layout.page_size} onValueChange={(v) => updateLayout({ page_size: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="paper-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PAPER_SIZES).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {layout.page_size === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] text-[#64748B]">Width (px)</Label>
                  <Input type="number" min={200} max={3000} value={layout.custom_width} onChange={(e) => updateLayout({ custom_width: +e.target.value || 200 })} className="h-7 text-xs" data-testid="paper-custom-w" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#64748B]">Height (px)</Label>
                  <Input type="number" min={200} max={3000} value={layout.custom_height} onChange={(e) => updateLayout({ custom_height: +e.target.value || 200 })} className="h-7 text-xs" data-testid="paper-custom-h" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-[11px] text-[#64748B]">Orientation</Label>
              <Select value={layout.orientation} onValueChange={(v) => updateLayout({ orientation: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="paper-orientation-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait" className="text-xs">Portrait</SelectItem>
                  <SelectItem value="landscape" className="text-xs">Landscape</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer pt-1">
              <input type="checkbox" checked={!!layout.snap} onChange={(e) => updateLayout({ snap: e.target.checked })} data-testid="paper-snap-toggle" />
              Snap to grid
            </label>
            {layout.snap && (
              <div>
                <Label className="text-[11px] text-[#64748B]">Grid (px)</Label>
                <Input type="number" min={2} max={64} value={layout.grid} onChange={(e) => updateLayout({ grid: +e.target.value || 8 })} className="h-7 text-xs" data-testid="paper-grid-size" />
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} data-testid="paper-zoom-out">−</Button>
              <span className="text-[11px] text-[#64748B] w-12 text-center">{Math.round(zoom * 100)}%</span>
              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setZoom(z => Math.min(2, z + 0.1))} data-testid="paper-zoom-in">+</Button>
            </div>
          </div>
        </div>

        <div className="careiq-card p-3 rounded-lg border border-[#E2E8F0] bg-white">
          <div className="text-xs font-semibold text-[#0A2342] mb-2">Add to canvas</div>
          <div className="grid grid-cols-2 gap-1.5">
            {ADDABLE_TYPES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => addFieldAtCenter(value)}
                className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] text-[#0A2342] border border-[#E2E8F0] rounded hover:bg-[#0E7490]/5 hover:border-[#0E7490] transition-colors"
                data-testid={`paper-add-${value}`}
              >
                <Icon className="w-3 h-3 text-[#0E7490]" /> {label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-2">New fields drop in the center — drag to position, drag the corner to resize.</p>
        </div>
      </div>

      {/* Center: canvas */}
      <div
        className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-lg p-6 overflow-auto"
        style={{ minHeight: 600, maxHeight: '80vh' }}
        data-testid="paper-canvas-wrapper"
      >
        {/* Convert-to-draggable banner — shown while there are still unplaced fields */}
        {fields.some(f => !f.canvas_position) && (
          <div className="mb-4 bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid="paper-convert-banner">
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs font-semibold text-[#92400E]">
                Convert this form to a draggable layout
              </div>
              <div className="text-[11px] text-[#78350F]">
                {fields.filter(f => !f.canvas_position).length} of {fields.length} field{fields.length === 1 ? '' : 's'} aren't on the paper yet. Auto-place them now and tweak by dragging.
              </div>
            </div>
            <Button
              size="sm"
              className="careiq-btn-primary h-8 text-xs"
              onClick={() => autoLayoutAll(false)}
              data-testid="paper-convert-btn"
            >
              Auto-place unplaced fields
            </Button>
          </div>
        )}
        {fields.length > 0 && !fields.some(f => !f.canvas_position) && (
          <div className="mb-4 flex justify-end">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => { if (window.confirm('Re-flow ALL fields into a fresh top-to-bottom layout? This overrides current positions.')) autoLayoutAll(true); }}
              data-testid="paper-reflow-btn"
            >
              Reset to auto layout
            </Button>
          </div>
        )}
        <div style={{ width: dims.w * zoom, height: dims.h * zoom, margin: '0 auto' }}>
          <div
            ref={canvasRef}
            className="bg-white shadow-md relative"
            style={{
              width: dims.w,
              height: dims.h,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              backgroundImage: gridBg,
              backgroundSize: layout.snap && layout.grid ? `${layout.grid}px ${layout.grid}px` : 'auto',
            }}
            onClick={() => setSelectedId(null)}
            data-testid="paper-canvas"
          >
            {fields.map((f, idx) => {
              const pos = f.canvas_position;
              if (!pos) return null;
              const isSel = f.field_id === selectedId;
              return (
                <FieldBox
                  key={f.field_id || idx}
                  field={f}
                  pos={pos}
                  isSelected={isSel}
                  onPointerDownMove={(e) => onPointerDown(e, idx, 'move')}
                  onPointerDownResize={(e) => onPointerDown(e, idx, 'resize')}
                  onRemove={() => removeField(idx)}
                  referencedField={f.field_type === 'field_reference' ? fields.find(x => x.field_id === f.reference_field_id) : null}
                  testid={`paper-field-${f.field_id}`}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: inspector */}
      <div className="space-y-3">
        {selected ? (
          <div className="careiq-card p-3 rounded-lg border border-[#E2E8F0] bg-white" data-testid="paper-inspector">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-[#0A2342]">Field properties</div>
              <button onClick={() => removeField(selectedIdx)} className="text-[#991B1B] hover:bg-[#991B1B]/5 p-1 rounded" data-testid="paper-inspector-delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-2">
              <div>
                <Label className="text-[11px] text-[#64748B]">Label</Label>
                <Input
                  value={selected.label || ''}
                  onChange={(e) => {
                    const next = [...fields];
                    next[selectedIdx] = { ...selected, label: e.target.value };
                    setFields(next);
                  }}
                  className="h-7 text-xs"
                  data-testid="paper-inspector-label"
                />
              </div>
              <div>
                <Label className="text-[11px] text-[#64748B]">Type</Label>
                <Select value={selected.field_type} onValueChange={(v) => {
                  const next = [...fields];
                  next[selectedIdx] = { ...selected, field_type: v };
                  setFields(next);
                }}>
                  <SelectTrigger className="h-7 text-xs" data-testid="paper-inspector-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ADDABLE_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selected.field_type === 'field_reference' && (
                <div>
                  <Label className="text-[11px] text-[#64748B]">References field</Label>
                  <Select
                    value={selected.reference_field_id || ''}
                    onValueChange={(v) => {
                      const next = [...fields];
                      next[selectedIdx] = { ...selected, reference_field_id: v };
                      setFields(next);
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs" data-testid="paper-inspector-ref"><SelectValue placeholder="Pick a field" /></SelectTrigger>
                    <SelectContent>
                      {referenceCandidates.length === 0 && <div className="px-2 py-1 text-[11px] text-[#94A3B8]">No other fields yet</div>}
                      {referenceCandidates.map(rf => (
                        <SelectItem key={rf.field_id} value={rf.field_id} className="text-xs">
                          {rf.label || rf.field_id} <span className="text-[#94A3B8]">({rf.field_type})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#94A3B8] mt-1">Mirrors that field's value when the form is filled.</p>
                </div>
              )}
              {['select', 'radio'].includes(selected.field_type) && (
                <div>
                  <Label className="text-[11px] text-[#64748B]">Options (comma-separated)</Label>
                  <Input
                    value={(selected.options || []).join(', ')}
                    onChange={(e) => {
                      const next = [...fields];
                      next[selectedIdx] = { ...selected, options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) };
                      setFields(next);
                    }}
                    className="h-7 text-xs"
                    data-testid="paper-inspector-options"
                  />
                </div>
              )}
              <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!selected.required}
                  onChange={(e) => {
                    const next = [...fields];
                    next[selectedIdx] = { ...selected, required: e.target.checked };
                    setFields(next);
                  }}
                  data-testid="paper-inspector-required"
                />
                Required
              </label>
              <div className="grid grid-cols-4 gap-1.5 pt-2 border-t border-[#F1F5F9]">
                <div>
                  <Label className="text-[10px] text-[#94A3B8]">X</Label>
                  <Input type="number" value={selected.canvas_position?.x || 0} onChange={(e) => setFieldPos(selectedIdx, { x: +e.target.value })} className="h-7 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#94A3B8]">Y</Label>
                  <Input type="number" value={selected.canvas_position?.y || 0} onChange={(e) => setFieldPos(selectedIdx, { y: +e.target.value })} className="h-7 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#94A3B8]">W</Label>
                  <Input type="number" value={selected.canvas_position?.w || 0} onChange={(e) => setFieldPos(selectedIdx, { w: +e.target.value })} className="h-7 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#94A3B8]">H</Label>
                  <Input type="number" value={selected.canvas_position?.h || 0} onChange={(e) => setFieldPos(selectedIdx, { h: +e.target.value })} className="h-7 text-xs" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="careiq-card p-4 rounded-lg border border-dashed border-[#E2E8F0] bg-white text-center" data-testid="paper-inspector-empty">
            <Move className="w-5 h-5 text-[#94A3B8] mx-auto mb-2" />
            <p className="text-[11px] text-[#64748B]">Click a field on the canvas to edit its properties, or use the palette to add new ones.</p>
          </div>
        )}

        {/* Field index — quick way to position a field that was added in the
            Form Fields tab but doesn't yet have a canvas position. */}
        <div className="careiq-card p-3 rounded-lg border border-[#E2E8F0] bg-white">
          <div className="text-xs font-semibold text-[#0A2342] mb-2">Field index ({fields.length})</div>
          <div className="max-h-[260px] overflow-y-auto space-y-1">
            {fields.length === 0 && <p className="text-[11px] text-[#94A3B8]">No fields yet.</p>}
            {fields.map((f, i) => (
              <button
                key={f.field_id || i}
                onClick={() => {
                  setSelectedId(f.field_id);
                  if (!f.canvas_position) {
                    // place at center on first reveal
                    setFieldPos(i, { x: 40, y: 40 + i * 56, w: 240, h: 40, page: 0 });
                  }
                }}
                className={`w-full text-left px-2 py-1.5 text-[11px] rounded transition-colors ${
                  selectedId === f.field_id ? 'bg-[#0E7490]/10 text-[#0E7490]' : 'text-[#0A2342] hover:bg-[#F1F5F9]'
                }`}
                data-testid={`paper-field-list-${f.field_id || i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">{f.label || `(${f.field_type})`}</span>
                  <span className="text-[10px] text-[#94A3B8] ml-2">
                    {f.canvas_position ? '●' : '○'}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-2"><FileText className="inline w-3 h-3 mr-0.5" /> ● placed · ○ not on canvas</p>
        </div>
      </div>
    </div>
  );
}

function FieldBox({ field, pos, isSelected, onPointerDownMove, onPointerDownResize, referencedField, testid }) {
  const isLabel = field.field_type === 'info' || field.field_type === 'field_reference';
  const placeholder = field.field_type === 'field_reference'
    ? (referencedField ? `{{${referencedField.label || referencedField.field_id}}}` : '⚠ pick a source field')
    : field.field_type === 'info'
      ? (field.info_text || field.label || 'Text label')
      : (field.placeholder || field.label || '');
  return (
    <div
      onMouseDown={onPointerDownMove}
      onTouchStart={onPointerDownMove}
      className={`absolute select-none ${isSelected ? 'ring-2 ring-[#0E7490]' : 'ring-1 ring-[#E2E8F0]'} bg-white/95 rounded shadow-sm cursor-move`}
      style={{ left: pos.x, top: pos.y, width: pos.w, height: pos.h }}
      data-testid={testid}
    >
      {/* Label strip */}
      <div className="px-1.5 py-0.5 text-[10px] text-[#0E7490] bg-[#0E7490]/5 border-b border-[#E2E8F0] flex items-center justify-between truncate">
        <span className="truncate">{field.label || field.field_type}{field.required && <span className="text-[#991B1B]"> *</span>}</span>
        <span className="text-[9px] text-[#94A3B8] ml-1 shrink-0">{field.field_type}</span>
      </div>
      {/* Body */}
      <div className="flex-1 text-[11px] text-[#475569] px-2 py-1 pointer-events-none" style={{ height: pos.h - 18 }}>
        {isLabel ? (
          <span className={field.field_type === 'field_reference' && !referencedField ? 'text-[#991B1B]' : 'text-[#0A2342]'}>{placeholder}</span>
        ) : field.field_type === 'checkbox' ? (
          <span>☐ {field.label}</span>
        ) : field.field_type === 'signature' ? (
          <span className="text-[#94A3B8] italic">(signature)</span>
        ) : (
          <span className="text-[#94A3B8] italic">{placeholder || `(${field.field_type})`}</span>
        )}
      </div>
      {/* Resize handle */}
      <div
        onMouseDown={onPointerDownResize}
        onTouchStart={onPointerDownResize}
        className="absolute right-0 bottom-0 w-3 h-3 bg-[#0E7490] rounded-tl cursor-se-resize"
        style={{ marginRight: -1, marginBottom: -1 }}
        data-testid={`${testid}-resize`}
      />
    </div>
  );
}
